import authService from './auth.service';
import { getStoredSession } from '../utils/sessionManager';

const BASE_URL = process.env.REACT_APP_SESSION_SERVICE_URL || 'http://localhost:3005';

function baseHeaders() {
  const session = getStoredSession();
  const headers = { 'Content-Type': 'application/json' };
  if (session?.orgId) headers['X-Org-Id'] = session.orgId;
  return headers;
}

/**
 * Resolve a short code to its session data.
 * Fully public — no auth required, works in incognito.
 */
export async function resolveShortCode(code) {
  const response = await fetch(`${BASE_URL}/${code}`, {
    method: 'GET',
    headers: { 'Content-Type': 'application/json' },
  });
  if (!response.ok) {
    const err = await response.json().catch(() => ({}));
    const error = new Error(err?.error?.message || `Could not resolve session (${response.status})`);
    error.code = err?.error?.code || null;
    error.redirectUrl = err?.error?.redirectUrl || null;
    throw error;
  }
  const data = await response.json();
  return data.data;
}
/**
 * Re-check whether a renderer is currently locked or deleted. Used to
 * re-validate on every mount of the renderer view, and to pick up a
 * lock/unlock/delete that happens mid-session.
 *
 * Both the lock and delete cases are SOFT: the endpoint returns HTTP 200 with
 * a flag instead of blocking. Locked → { locked: true, lockedByName, lockedAt }
 * (the renderer view still renders read-only). Deleted → { deleted: true }
 * (the caller clears the stale renderer selection and opens the picker instead
 * of hard-blocking the app). A 503 (Renderer Service unavailable) is treated
 * as unknown — we don't block on a failed check. Returns null when
 * unlocked/not-deleted/indeterminate.
 */
export async function checkRendererLockStatus(rendererId) {
  if (!rendererId) return null;

  const response = await authService.authenticatedFetch(
    `${BASE_URL}/v1/sessions/renderer-lock/${rendererId}`,
    { method: 'GET', headers: baseHeaders() }
  );

  // 503 (unavailable) and any other non-OK status → unknown; don't block.
  if (!response.ok) return null;

  const body = await response.json().catch(() => ({}));
  const data = body?.data || {};

  if (data.deleted) return { deleted: true };

  return data.locked
    ? {
        locked: true,
        lockedByName: data.lockedByName || null,
        lockedAt: data.lockedAt || null,
      }
    : null;
}

/**
 * Generate or retrieve a short share URL for a session.
 */
export async function shareBBSession(sessionId) {
  const response = await authService.authenticatedFetch(
    `${BASE_URL}/v1/sessions/${sessionId}/share`,
    { method: 'POST', headers: baseHeaders() }
  );

  if (!response.ok) {
    const err = await response.json().catch(() => ({}));
    throw new Error(err?.error?.message || `Share URL generation failed (${response.status})`);
  }

  const data = await response.json();
  return data.data;
}

const CLIENT_EVENT_TIMEOUT_MS = 3000;

/**
 * Report a client-side activity against a session (observability). Unlike
 * every other function in this file, this one is meant to be called
 * fire-and-forget — see logClientSessionEvent below, which is the function
 * call sites should actually use.
 */
export async function postSessionEvent(sessionId, { eventType, source, metadata, occurredAt, changes }) {
  const controller = new AbortController();
  const timeout = setTimeout(() => controller.abort(), CLIENT_EVENT_TIMEOUT_MS);
  try {
    const response = await authService.authenticatedFetch(
      `${BASE_URL}/v1/sessions/${sessionId}/events`,
      {
        method: 'POST',
        headers: baseHeaders(),
        body: JSON.stringify({ eventType, source, metadata, occurredAt, changes }),
        signal: controller.signal,
      }
    );
    if (!response.ok) {
      throw new Error(`Session event ingestion failed (${response.status})`);
    }
  } finally {
    clearTimeout(timeout);
  }
}

/**
 * Fire-and-forget wrapper around postSessionEvent for use directly at call
 * sites (goal/card/sub triggers, toggle handlers) — never throws, never
 * needs an await or a .catch() from the caller, and no-ops silently when no
 * sessionId is available (not every launch path has one). This must stay
 * cheap and side-effect-free on failure: it must never surface an error to
 * the operator or delay/interfere with the action it's reporting on.
 *
 * `changes`, when given, is an optional { fieldName: { from, to } } diff —
 * e.g. an operator editing a goal's player/goal_type — rendered in the audit
 * history the same way session-service's own field-edit events are.
 */
export function logClientSessionEvent(sessionId, eventType, source, metadata, changes) {
  if (!sessionId) return;
  postSessionEvent(sessionId, { eventType, source, metadata, changes }).catch(() => {});
}
